import TweenLite, { Power1 } from 'gsap/TweenLite';
import TimelineLite from 'gsap/TimelineLite';
import CSSPlugin from 'gsap/CSSPlugin';
import { debounce } from 'throttle-debounce';
import stepsScripts from './steps';
import { breakpointsNames } from './breakpoints';
import passiveEvents from '../utils/passive-events';
// import TimelineLite from 'gsap/TimelineLite';

// eslint-disable-next-line no-unused-vars
const plugins = [CSSPlugin];

const SCROLL_ACCEPTABLE_DIFF = 5;

// const plugins = [CSSPlugin, AttrPlugin];
const breakpoints = breakpointsNames;

export default class Presentation {
  constructor(presentations, el, breakpoint) {
    this.presentations = presentations;
    this.el = el;
    this.breakpoint = breakpoint;
    this.steps = {};
    this.stepsByBreakpoint = {};
    this.stepsIdsByBreakpoint = {};
    this.elements = {};
    this.timeline = null;
    this.ignoreActions = false;
    this.playStartTimeline = null;

    this.setupBreakpoints();
    this.indexElements();
    this.initializeStartAnimation();
    this.loadAndInitializeStepsScripts();
    this.createTimeline();
    this.setupNavigation();
    this.setupActionLock();
    this.setupInputs();

    if (process.env.NODE_ENV !== 'production') {
      window.pres = this;
    }
  }

  get currentSteps() {
    return this.stepsByBreakpoint[this.breakpoint];
  }

  get currentsStepsIds() {
    return this.stepsIdsByBreakpoint[this.breakpoint];
  }

  get currentId() {
    return this.currentsStepsIds[Math.floor(this.timeline.time())];
  }

  get currentStep() {
    return this.currentSteps[this.currentId];
  }

  setupBreakpoints() {
    breakpoints.forEach(breakpoint => {
      this.stepsByBreakpoint[breakpoint] = {};
      this.stepsIdsByBreakpoint[breakpoint] = [];
    });
  }

  indexElements() {
    const { elements } = this;
    // debugger;
    Array.from(this.el.querySelectorAll('[data-element]')).forEach(element => {
      const elementName = element.getAttribute('data-element');
      if (!elementName) return;
      if (Array.isArray(elements[elementName])) {
        elements[elementName].push(element);
      } else if (elements[elementName]) {
        elements[elementName] = [elements[elementName], element];
      } else {
        elements[elementName] = element;
      }
    });

    elements.logoLetters = [elements.logoO, elements.logoA, elements.logoW];
    elements.logoLettersBoxes = elements.logoLetters.map(el =>
      el.getBoundingClientRect(),
    );
  }

  initializeStartAnimation() {
    let { startAnimation } = stepsScripts[0];

    if (!startAnimation) {
      startAnimation = () => {};
    }

    let timelineResolve = null;
    const timelinePromise = new Promise(res => {
      timelineResolve = res;
    });

    const startTimeline = new TimelineLite({
      paused: true,
      onComplete: () => {
        timelineResolve();

        setTimeout(() => {
          if (
            this.presentations.presentation === this &&
            this.timeline.paused() &&
            this.currentId === 'start'
          ) {
            this.next();
          }
        }, 3000);
      },
      // onComplete: () => {},
    });
    startAnimation(this, startTimeline);
    startTimeline.progress(100, true);

    this.playStartTimeline = () => {
      // console.timeStamp('Intro start1');
      startTimeline.progress(0, true);
      document.body.style.visibility = 'inherit';

      const start = () => {
        // console.timeStamp('Intro start2');
        startTimeline.play();
      };

      if (window.requestIdleCallback) {
        window.requestIdleCallback(start, { timeout: 1000 });
      } else {
        window.setTimeout(start, 0);
      }

      window.startTimeline = startTimeline;

      return timelinePromise;
    };
  }

  loadAndInitializeStepsScripts() {
    stepsScripts.forEach(stepScript => {
      stepScript(this);
    });
  }

  setupNavigation() {
    Array.from(this.el.querySelectorAll('.js-presentation-navigate')).forEach(
      btn => {
        const { target } = btn.dataset;
        btn.addEventListener('click', e => {
          e.preventDefault();
          this.presentations.fade(target);
        });
      },
    );
  }

  setupActionLock() {
    const isNotScrolled = (el, direction) =>
      (direction === 1 &&
        el.scrollHeight - el.offsetHeight - el.scrollTop >
          SCROLL_ACCEPTABLE_DIFF) ||
      (direction === -1 && el.scrollTop > SCROLL_ACCEPTABLE_DIFF);

    const opts = passiveEvents ? { passive: false } : false;

    Array.from(this.el.querySelectorAll('.js-scrollable')).forEach(el => {
      const scrollEnded = debounce(100, () => {
        this.ignoreActions = false;
        // console.log('not ignoring');
      });

      el.addEventListener('scroll', () => {
        // Ignore scroll events for bouncing on safari
        if (
          el.scrollTop < SCROLL_ACCEPTABLE_DIFF ||
          el.scrollHeight - el.offsetHeight - el.scrollTop <
            SCROLL_ACCEPTABLE_DIFF
        ) {
          return;
        }

        if (!this.ignoreActions && this.time() % 1 === 0) {
          this.ignoreActions = true;
          // console.log('ignoring');
        }
        scrollEnded();
        // console.log('pres-scroll');
      });

      el.addEventListener(
        'wheel',
        e => {
          if (this.ignoreActions) {
            return;
          }
          const direction = Math.sign(e.deltaY);
          if (isNotScrolled(el, direction)) {
            // console.log('wheelcheck');
            e.stopPropagation();
          }
        },
        opts,
      );

      let startTouch = null;
      el.addEventListener(
        'touchstart',
        e => {
          startTouch = e.touches && e.touches.length === 1 && e.touches[0];
        },
        opts,
      );

      el.addEventListener(
        'touchmove',
        e => {
          if (this.ignoreActions) {
            e.stopPropagation();
            return;
          }

          const secondTouch =
            e.touches && e.touches.length === 1 && e.touches[0];

          if (
            startTouch &&
            secondTouch &&
            startTouch.identifier === secondTouch.identifier
          ) {
            const direction = Math.sign(startTouch.pageY - secondTouch.pageY);
            if (isNotScrolled(el, direction) && this.time() % 1 === 0) {
              // console.log('touchcheck');
              e.stopPropagation();
            }
          }
        },
        opts,
      );
    });
  }

  setupInputs() {
    let fieldsCount = 0;
    Array.from(this.el.querySelectorAll('input, textarea')).forEach(el => {
      const update = val => {
        fieldsCount += val;
        this.presentations.ignoreResize = fieldsCount > 0;
      };

      el.addEventListener('focus', () => {
        update(1);
      });

      el.addEventListener('blur', () => {
        setTimeout(() => {
          update(-1);
        }, 100);
      });
    });

    Array.from(this.el.querySelectorAll('textarea')).forEach(el => {
      // https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/101525/
      // eslint-disable-next-line no-param-reassign
      el.value = '';
    });
  }

  createTimeline() {
    const { breakpoint, stepsIdsByBreakpoint, stepsByBreakpoint } = this;

    TweenLite.defaultEase = Power1.easeInOut;
    // Reverse easing when touching
    // for(let i = 1; i < 10000; i++) { console.log(Power1.easeInOut.getRatio(i/10000) === getRatio(i/10000)) }
    // for(let i = 1; i < 10000; i++) { console.log(i/10000 === getRatioReverse(getRatio(i/10000)), getRatio(i/10000), i/10000, getRatioReverse(getRatio(i/10000))) }
    // const getRatio = p => {
    //   const r = (p < 0.5 ? p * 2 : (1 - p) * 2) ** 2;
    //   // console.log(r);
    //   return p < 0.5 ? r / 2 : 1 - r / 2;
    // };
    this.easeReverse = ratio => {
      const r = ratio < 0.5 ? ratio * 2 : (1 - ratio) * 2;
      // console.log(r);
      const rr = Math.sqrt(r);
      // console.log(rr);
      return ratio < 0.5 ? rr / 2 : 1 - rr / 2;
    };
    const timeline = new TimelineLite({ paused: true });

    let previousStepHelpers = null;

    stepsIdsByBreakpoint[breakpoint].forEach((stepId, stepIndex) => {
      const startTime = Math.max(0, stepIndex - 1);
      const step = stepsByBreakpoint[breakpoint][stepId];
      const stepLabel = `step${stepId.replace(/^[a-z]/, ([letter]) =>
        letter.toUpperCase(),
      )}`;
      step.label = stepLabel;

      // console.log(stepLabel, startTime, timeline.duration());

      if (timeline.duration() > startTime) {
        throw new Error(
          'It looks like something is taking more time that it should, each transition should take one second',
        );
      }

      if (stepIndex > 1) {
        timeline.addPause(startTime, () => {
          // console.log('pause', stepIndex);
          this.presentations.downloadImages();
        });
      }

      timeline.add(stepLabel, startTime);

      if (previousStepHelpers) {
        previousStepHelpers.leave({
          timeline,
          originalLabel: previousStepHelpers.label,
          label: stepLabel,
        });
      }

      if (step.enter) {
        step.enter({ timeline, label: stepLabel });
      }

      previousStepHelpers = step.leave
        ? { label: stepLabel, leave: step.leave }
        : null;
    });

    // Add to top button
    // timeline.to(this.elements.toTop, 1, { autoAlpha: 1 }, 0);

    this.timeline = timeline;
  }

  addStep(step) {
    // { id, default: {  }, medium:, ...  }

    const { id } = step;
    if (this.steps[id]) {
      throw new Error('Step with this id exists');
    }

    this.steps[id] = step;

    let fromPreviousBreakpoint = null;
    breakpoints.forEach(breakpoint => {
      let stepBreakpoint = fromPreviousBreakpoint;
      if (step[breakpoint] === null) {
        stepBreakpoint = null;
      } else if (typeof step[breakpoint] === 'object') {
        stepBreakpoint = step[breakpoint];
      }

      if (stepBreakpoint) {
        this.stepsByBreakpoint[breakpoint][id] = stepBreakpoint;
        this.stepsIdsByBreakpoint[breakpoint].push(id);
      }

      fromPreviousBreakpoint = stepBreakpoint;
    });
  }

  prev() {
    if (this.ignoreActions) {
      return;
    }

    this.timeline.reverse();
  }

  next() {
    if (this.ignoreActions) {
      return;
    }

    this.timeline.pause().play();
  }

  time(...args) {
    if (this.ignoreActions && args.length !== 0) {
      return this.timeline.time();
    }
    return this.timeline.time(...args);
  }

  seek(idOrIndex) {
    let index = -1;
    if (typeof idOrIndex === 'number') {
      index = idOrIndex;
    } else if (idOrIndex === 'nextToLast') {
      index = this.currentsStepsIds.length - 2;
    } else if (typeof idOrIndex === 'string') {
      index = this.currentsStepsIds.indexOf(idOrIndex);
    }

    if (index === -1 || index >= this.currentSteps.length) {
      return;
    }

    this.timeline.pause().seek(index, false);
  }
}



// WEBPACK FOOTER //
// ./src/scripts/modules/Presentation.js